import { AlertTriangle, Archive, Ban, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

/**
 * El estado de una empresa en la lista. Una archivada es otra cosa que una inactiva (suspendida):
 * se muestra como «Archivada», aunque su `isActive` siga en true.
 */
export function EstadoDeLaEmpresa({
  activa,
  archivadaEl,
}: {
  activa: boolean;
  archivadaEl?: string | null;
}) {
  if (archivadaEl) {
    return (
      <Badge variant="outline" className="text-muted-foreground">
        <Archive className="h-3 w-3 mr-1" aria-hidden="true" /> Archivada
      </Badge>
    );
  }
  return activa ? (
    <Badge variant="outline" className="text-green-500 border-green-500/30">
      <CheckCircle className="h-3 w-3 mr-1" aria-hidden="true" /> Activo
    </Badge>
  ) : (
    <Badge variant="destructive">
      <Ban className="h-3 w-3 mr-1" aria-hidden="true" /> Inactivo
    </Badge>
  );
}

/**
 * La vigencia de una suscripción, en palabras y no solo en color.
 *
 * Se muestra cuánto falta —"vence en 27 días"— porque es la pregunta real; la
 * fecha sola obliga a hacer la cuenta. Y el estado va con ícono además de color.
 */
export function Vigencia({
  endDate,
  archivada,
  esDemo,
}: {
  endDate?: string | null;
  archivada?: boolean;
  esDemo?: boolean;
}) {
  // Sin plazo que mostrar: una archivada está fuera de uso a propósito. Una demo sí tiene plazo
  // (2-nov: vence a los 15 días, como la prueba); quien llama pasa el de `demo.vencimiento`.
  void esDemo;
  if (archivada || !endDate) return <span className="text-muted-foreground">—</span>;

  const fin = new Date(endDate);
  const dias = Math.ceil((fin.getTime() - Date.now()) / 86_400_000);
  const fecha = fin.toLocaleDateString('es-BO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  // Vencido, por vencer (una semana) y vigente. Tres estados, tres señales.
  const vencido = dias < 0;
  const porVencer = dias >= 0 && dias <= 7;

  return (
    <div className="flex flex-col gap-0.5">
      <span className="flex items-center gap-1 text-sm">
        {vencido ? (
          <Ban className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
        ) : porVencer ? (
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600" aria-hidden="true" />
        ) : null}
        <span className={vencido ? 'text-destructive' : porVencer ? 'text-amber-600' : undefined}>
          {fecha}
        </span>
      </span>
      <span className="text-xs text-muted-foreground">
        {vencido
          ? `vencida hace ${Math.abs(dias)} ${Math.abs(dias) === 1 ? 'día' : 'días'}`
          : dias === 0
            ? 'vence hoy'
            : `vence en ${dias} ${dias === 1 ? 'día' : 'días'}`}
      </span>
    </div>
  );
}

/**
 * El número de «Usuarios» de la lista. Una archivada no tiene a nadie con acceso (archivar lo
 * apaga), así que un «0» sugeriría una empresa vacía: se muestra «—», como la vigencia.
 */
export function UsuariosDeLaEmpresa({
  cantidad,
  archivada,
}: {
  cantidad: number;
  archivada?: boolean;
}) {
  return archivada ? <span className="text-muted-foreground">—</span> : <>{cantidad}</>;
}
